import React from 'react';
import {
  Flex,
  Button,
  Text,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  VStack,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { useDisplayCurrency } from '../../contexts/DisplayCurrencyContext';

/**
 * MI-298: app-level display-currency control. "Per currency" keeps the existing
 * per-currency view; picking a currency switches totals surfaces to a single
 * approximate total converted into it. Each load opens on the user's home
 * currency when one resolves (MI-311), on "per currency" otherwise.
 *
 * Deliberately a dropdown rather than a pill strip: it sits beside the
 * per-currency *filter* tabs, which are pills. Converting and filtering are
 * different operations — one changes the unit, the other changes the data — so
 * they get different affordances instead of two identical-looking strips
 * listing the same currency codes. It also scales past the two or three
 * currencies a horizontal strip can hold.
 */
export default function DisplayCurrencySelector() {
  const { t } = useTranslation('common');
  // The list comes from the context, which already loads it for the
  // home-currency default (MI-311) — one request instead of two.
  const { displayCurrency, setDisplayCurrency, activeCurrencies: currencies } = useDisplayCurrency();

  // Nothing to convert between with fewer than two currencies.
  if (currencies.length < 2) return null;

  const selected = displayCurrency || 'all';
  const options = ['all', ...currencies];
  const label = (c) => (c === 'all' ? t('convert.perCurrency') : `≈ ${c}`);

  return (
    <Popover placement="bottom-end" strategy="fixed">
      {({ onClose }) => (
        <>
          <PopoverTrigger>
            <Button
              size="sm"
              variant="ghost"
              height="28px"
              px={2}
              minW="auto"
              borderRadius="md"
              fontSize={{ base: 'xs', md: 'sm' }}
              fontWeight={displayCurrency ? 600 : 400}
              color={displayCurrency ? 'redesign.heading' : 'redesign.text.muted'}
              bg={displayCurrency ? 'redesign.bg.card' : 'transparent'}
              _hover={{ bg: 'redesign.bg.card', color: 'redesign.heading' }}
              rightIcon={<ChevronDownIcon />}
              aria-label={t('convert.convertTo')}
            >
              {/* Idle, the trigger names what the control does — "Por moneda"
                  read as just another filter next to the currency tabs. Active,
                  it shows the currency instead, since that is the only thing on
                  screen saying which unit every figure is in. */}
              {displayCurrency ? `≈ ${displayCurrency}` : t('convert.convertTo')}
            </Button>
          </PopoverTrigger>
          <PopoverContent w="fit-content" minW="150px" borderRadius="lg">
            <PopoverBody p={2}>
              <Text textStyle="microLabel" px={2} pb={1}>
                {t('convert.convertTo')}
              </Text>
              <VStack align="stretch" spacing={0.5}>
                {options.map((c) => (
                  <Flex
                    key={c}
                    as="button"
                    type="button"
                    align="center"
                    px={2}
                    py="5px"
                    borderRadius="md"
                    fontSize="sm"
                    textAlign="left"
                    bg={selected === c ? 'redesign.bg.card' : 'transparent'}
                    color={selected === c ? 'redesign.heading' : 'redesign.text'}
                    fontWeight={selected === c ? 600 : 400}
                    _hover={{ bg: 'redesign.bg.hover' }}
                    onClick={() => { setDisplayCurrency(c); onClose(); }}
                  >
                    {label(c)}
                  </Flex>
                ))}
              </VStack>
            </PopoverBody>
          </PopoverContent>
        </>
      )}
    </Popover>
  );
}
